/**
 * GremlinAssistant
 *
 * A small floating button in the bottom-right corner of the dashboard.
 * Click to open a slide-up chat panel powered by Claude via the
 * ai-assistant edge function.  Knows everything about SocialScoop.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SYSTEM_PROMPT = `You are Gremlin, SocialScoop's helpful in-app assistant. You are concise, warm, and slightly playful. You know everything about SocialScoop's features:

- Listening Rules: plain-English rules users write to surface content they want. Have an Intensity Gauge (0-5 slider).
- Digital Fencing (Fences): rules to de-emphasize or remove content users don't want. Content goes to the Content Vault — never deleted.
- Content Vault: PIN-protected area holding all fenced content. Fully reviewable, recoverable.
- Supported platforms: Bluesky and YouTube (free); Reddit and X on paid plans.
- Feed updates every 20 minutes automatically.
- AI scoring: Claude analyzes sentiment and toxicity as advisory information — it never overrides user rules.
- Curator feeds: Invitation-only curators share public curated feeds. Users can follow them. Founding Members get early curator access.
- Pricing: Free tier (Bluesky + YouTube, 5 rules, 5 fences); Personal $9/mo or $6.58/mo annual (Reddit + X, 20 rules, 20 fences); Family $15/mo (all platforms, unlimited); Founding Member $49/year (first year, 500 spots).
- Family Mode: Parent Lock, PIN-protected child profiles, rules children cannot override.
- Onboarding: Connect platforms → Set rules → Feed updates automatically.
- Ambient dashboard: background themes, ambient music (Lo-fi / Nature / Piano / Silence), custom accent colors.

Keep responses under 3 short paragraphs. Use plain text — no markdown headers or bullet lists. If you don't know something, say so honestly. Never make up features.`;

const WELCOME: Message = {
  role: 'assistant',
  content: "Hey! I'm Gremlin, your SocialScoop helper. Ask me anything — how to set up rules, what Fencing does, how the AI scoring works, anything. I'm here.",
};

// ── Gremlin icon SVG ──────────────────────────────────────────────────────────

const GremlinIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none" aria-hidden>
    {/* Body */}
    <ellipse cx="18" cy="22" rx="11" ry="9" fill="#1A2E4A" />
    {/* Head */}
    <circle cx="18" cy="13" r="9" fill="#1A2E4A" />
    {/* Ears */}
    <path d="M9 8 L5 2 L12 7Z" fill="#1A2E4A" />
    <path d="M27 8 L31 2 L24 7Z" fill="#1A2E4A" />
    {/* Eyes */}
    <ellipse cx="14.5" cy="12.5" rx="2.5" ry="2.5" fill="white" />
    <ellipse cx="21.5" cy="12.5" rx="2.5" ry="2.5" fill="white" />
    <circle cx="15.2" cy="12.8" r="1.2" fill="#1A2E4A" />
    <circle cx="22.2" cy="12.8" r="1.2" fill="#1A2E4A" />
    {/* Smile */}
    <path d="M14 17 Q18 20 22 17" stroke="white" strokeWidth="1.4" fill="none" strokeLinecap="round" />
  </svg>
);

// ── Message bubble ────────────────────────────────────────────────────────────

const Bubble: React.FC<{ msg: Message }> = ({ msg }) => (
  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div
      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        msg.role === 'user'
          ? 'bg-foreground text-background rounded-br-sm'
          : 'bg-muted text-foreground rounded-bl-sm'
      }`}
    >
      {msg.content}
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

export const GremlinAssistant: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Stop pulsing after first open
  useEffect(() => {
    if (open) setPulse(false);
  }, [open]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: Message = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = [...messages, userMsg].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          action: 'gremlin_chat',
          system_prompt: SYSTEM_PROMPT,
          messages: history,
        },
      });

      const reply: string =
        data?.reply || data?.response || data?.message ||
        "Sorry, I had trouble thinking that through. Try again?";

      if (error) throw error;

      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "Hmm, something went wrong on my end. Try asking again in a moment.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => setMessages([WELCOME]);

  return createPortal(
    <div style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 9000 }}>
      {/* Chat panel */}
      {open && (
        <div
          className="mb-3 w-[340px] max-w-[calc(100vw-48px)] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-3 fade-in duration-200"
          style={{ height: 460 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-[#1A2E4A]">
            <div className="flex items-center gap-2.5">
              <GremlinIcon size={20} />
              <span className="text-sm font-semibold text-white">Gremlin</span>
              <span className="text-[10px] text-white/50 uppercase tracking-wider">AI helper</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={reset}
                className="text-white/50 hover:text-white/90 transition-colors p-1"
                title="New conversation"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-white/50 hover:text-white/90 transition-colors p-1"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((msg, i) => (
              <Bubble key={i} msg={msg} />
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-2.5">
                  <span className="inline-flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t border-border px-3 py-3 flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask me anything…"
              rows={1}
              className="flex-1 resize-none text-sm bg-transparent placeholder:text-muted-foreground/50 focus:outline-none leading-relaxed py-1 max-h-28"
              style={{ fieldSizing: 'content' } as React.CSSProperties}
            />
            <Button
              size="icon"
              className="h-8 w-8 shrink-0 bg-[#1A2E4A] hover:bg-[#1A2E4A]/80"
              onClick={send}
              disabled={!input.trim() || loading}
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Floating trigger button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`relative w-14 h-14 rounded-full bg-[#1A2E4A] shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center ${
          open ? 'shadow-xl' : ''
        }`}
        aria-label="Open Gremlin AI assistant"
      >
        {open ? (
          <X className="h-5 w-5 text-white" />
        ) : (
          <GremlinIcon size={26} />
        )}

        {/* Pulse ring for first-visit nudge */}
        {pulse && !open && (
          <>
            <span className="absolute inset-0 rounded-full bg-[#1A2E4A] animate-ping opacity-30" />
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-background" />
          </>
        )}
      </button>
    </div>,
    document.body,
  );
};

export default GremlinAssistant;
